import React, { useState, useEffect } from 'react';
import { Settings, Save, AlertCircle, CheckCircle, RefreshCw, XCircle } from 'lucide-react';
import { n8nWebhookLogsService } from '../../../services/n8nWebhookLogsService';

export default function RetryPolicyConfig({ onClose }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const currentConfig = await n8nWebhookLogsService?.getRetryPolicyConfig();
      setConfig(currentConfig);
    } catch (err) {
      setError(`Erreur chargement configuration: ${err?.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // Validate configuration
      if (config?.maxRetries < 1 || config?.maxRetries > 20) {
        setError('Le nombre max de retries doit être entre 1 et 20');
        return;
      }

      if (config?.baseDelay < 100 || config?.baseDelay > 60000) {
        setError('Le délai de base doit être entre 100ms et 60000ms');
        return;
      }

      if (config?.backoffMultiplier < 1 || config?.backoffMultiplier > 10) {
        setError('Le multiplicateur de backoff doit être entre 1 et 10');
        return;
      }

      await n8nWebhookLogsService?.updateRetryPolicyConfig(config);
      setSuccess('✅ Configuration sauvegardée avec succès !');
      
      setTimeout(() => {
        if (onClose) onClose();
      }, 2000);
    } catch (err) {
      setError(`Erreur sauvegarde: ${err?.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const toggleStatusCode = (code) => {
    setConfig(prev => {
      const codes = prev?.retryableStatusCodes || [];
      if (codes?.includes(code)) {
        return { ...prev, retryableStatusCodes: codes?.filter(c => c !== code) };
      } else {
        return { ...prev, retryableStatusCodes: [...codes, code]?.sort((a, b) => a - b) };
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg max-w-4xl mx-auto">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center">
          <Settings className="w-6 h-6 text-green-600 mr-3" />
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Configuration Politique de Relance
            </h2>
            <p className="text-sm text-gray-600">
              Paramètres avancés pour la gestion automatique des échecs webhook
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <XCircle className="w-6 h-6" />
        </button>
      </div>

      <div className="p-6 space-y-6">
        {/* Error/Success Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start">
            <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
            <p className="text-green-800 text-sm">{success}</p>
          </div>
        )}

        {/* Basic Retry Settings */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Paramètres de Base</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre Maximum de Retries
              </label>
              <input
                type="number"
                min="1"
                max="20"
                value={config?.maxRetries}
                onChange={(e) => handleChange('maxRetries', parseInt(e?.target?.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Nombre d'essais avant échec permanent (1-20)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Seuil d'Échecs Consécutifs
              </label>
              <input
                type="number"
                min="1"
                max="50"
                value={config?.failureThreshold}
                onChange={(e) => handleChange('failureThreshold', parseInt(e?.target?.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Échecs avant déplacement vers DLQ (1-50)
              </p>
            </div>
          </div>
        </div>

        {/* Exponential Backoff Settings */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Backoff Exponentiel</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Délai de Base (ms)
              </label>
              <input
                type="number"
                min="100"
                max="60000"
                step="100"
                value={config?.baseDelay}
                onChange={(e) => handleChange('baseDelay', parseInt(e?.target?.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Délai initial avant 1er retry
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Multiplicateur
              </label>
              <input
                type="number"
                min="1"
                max="10"
                step="0.5"
                value={config?.backoffMultiplier}
                onChange={(e) => handleChange('backoffMultiplier', parseFloat(e?.target?.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Facteur d'augmentation du délai
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Délai Maximum (ms)
              </label>
              <input
                type="number"
                min="1000"
                max="300000"
                step="1000"
                value={config?.maxDelay}
                onChange={(e) => handleChange('maxDelay', parseInt(e?.target?.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Délai max entre retries
              </p>
            </div>
          </div>

          {/* Backoff Calculation Preview */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm font-medium text-blue-900 mb-2">Aperçu des Délais de Retry:</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs text-blue-700">
              {[...Array(Math?.min(config?.maxRetries, 5))]?.map((_, i) => {
                const delay = Math?.min(
                  config?.baseDelay * Math?.pow(config?.backoffMultiplier, i),
                  config?.maxDelay
                );
                return (
                  <div key={i} className="bg-white rounded p-2">
                    <span className="font-semibold">Retry {i + 1}:</span>
                    <br />
                    {(delay / 1000)?.toFixed(1)}s
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Retryable Status Codes */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Codes HTTP à Relancer</h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[408, 429, 500, 502, 503, 504, 520, 522, 524]?.map(code => (
              <label key={code} className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config?.retryableStatusCodes?.includes(code)}
                  onChange={() => toggleStatusCode(code)}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <span className="text-sm text-gray-700 font-mono">{code}</span>
              </label>
            ))}
          </div>
          
          <p className="text-xs text-gray-500">
            Sélectionnez les codes HTTP qui déclencheront un retry automatique
          </p>
        </div>

        {/* Dead Letter Queue */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Dead Letter Queue (DLQ)</h3>
          
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={config?.enableDeadLetterQueue}
              onChange={(e) => handleChange('enableDeadLetterQueue', e?.target?.checked)}
              className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
            />
            <div>
              <span className="text-sm font-medium text-gray-700">
                Activer la Dead Letter Queue
              </span>
              <p className="text-xs text-gray-500">
                Les webhooks échoués définitivement seront déplacés vers une file d'attente spéciale pour analyse et retraitement manuel
              </p>
            </div>
          </label>
        </div>

        {/* Summary Card */}
        <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Résumé de la Configuration</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Max Retries:</span>
              <span className="font-semibold text-gray-900">{config?.maxRetries}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Délai Initial:</span>
              <span className="font-semibold text-gray-900">{(config?.baseDelay / 1000)?.toFixed(1)}s</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Délai Maximum:</span>
              <span className="font-semibold text-gray-900">{(config?.maxDelay / 1000)?.toFixed(1)}s</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Codes Retryables:</span>
              <span className="font-semibold text-gray-900">{config?.retryableStatusCodes?.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Seuil Échecs:</span>
              <span className="font-semibold text-gray-900">{config?.failureThreshold}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">DLQ Activée:</span>
              <span className="font-semibold text-gray-900">
                {config?.enableDeadLetterQueue ? '✅ Oui' : '❌ Non'}
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            <Save className={`w-5 h-5 mr-2 ${saving ? 'animate-spin' : ''}`} />
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
      </div>
    </div>
  );
}